import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { PolicyFields } from "../../../../enums/policy-fields.enum";
import { RiskItemInformations } from "../../../../enums/riskItemInformations";
import { PolicyFieldsVM } from "../../../../models/policy-field-vm.model";
import {
  getIsVisible,
  getIsEditable,
  getInputClass,
  getDropdownClass,
} from "../../../../helpers/contract-edition.helper";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { PolicyDetail } from "../../../../models/policy-details";
import { DropdownModule } from "primeng/dropdown";
import { HouseType } from "../../../../enums/houseType";

@Component({
  selector: "app-contract-edition-housing-mrh",
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, DropdownModule, ReactiveFormsModule],
  templateUrl: "./contract-edition-housing-mrh.html",
})
export class ContractEditionHousingMrhComponent {
  @Input() policyFieldsVM!: PolicyFieldsVM;
  @Input() policyDetails!: PolicyDetail;
  @Input() policyDetailsCopy!: PolicyDetail;
  @Input() possedePiscine!: string | undefined;
  @Input() natureLogement!: string | undefined;
  @Input() possedeEnergieV!: string | undefined;
  @Input() qualiteOccupant!: string | undefined;
  @Input() typeHabitation!: string | undefined;
  @Input() nombrePieces!: string | undefined;
  @Input() possedeVeranda!: string | undefined;
  @Input() nombrePiecesQuarante!: string | undefined;
  @Input() surfaceDependance!: string | undefined;
  @Input() etage!: string;
  @Input() isConfort!: boolean | undefined;
  @Input() hasEndorsementWait4Sign!: boolean;
  @Output() emitNatureLogement = new EventEmitter<string>();
  @Output() emitQualiteOccupant = new EventEmitter<string>();
  @Output() emitNombrePieces = new EventEmitter<string>();
  @Output() emitNombrePiecesQuarante = new EventEmitter<string>();
  @Output() emitSurfaceDependance = new EventEmitter<string>();
  @Output() emitPossedeVeranda = new EventEmitter<string>();
  @Output() emitPossedeEnergieV = new EventEmitter<string>();
  @Output() emitPossedePiscine = new EventEmitter<string>();

  PolicyFields = PolicyFields;
  riskItemInfo = RiskItemInformations;
  HouseType = HouseType;
  getIsVisible = getIsVisible;
  getIsEditable = getIsEditable;
  getInputClass = getInputClass;
  getDropdownClass = getDropdownClass;

  natureResidenceOptions = ["Principale", "Secondaire"];
  qualiteOccupantOptions = ["Propriétaire", "Locataire"];
  nombrePiecesOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
  nombrePiecesQuaranteOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
  dependanceOptions = ["Aucune", "0-50m2", "50-100m2", "100-250m2"];
  etageOptions = ["Rez De Chaussée", "Intermédiaire", "Dernier étage"];
  falseTrueValues: { label: string; value: string }[] = [
    { label: "Non", value: "False" },
    { label: "Oui", value: "True" },
  ];

  policyAddressHasChanged = false;
  policyAddressComplementHasChanged = false;
  policyZipCodeHasChanged = false;
  policyCityHasChanged = false;
  natureResidenceHasChanged = false;
  qualiteOccupantHasChanged = false;
  typeHabitationHasChanged = false;
  nombrePiecesHasChanged = false;
  nombrePiecesQuaranteHasChanged = false;
  surfaceDependanceHasChanged = false;
  etageHasChanged = false;
  possedePiscineHasChanged = false;
  possedeVerandaHasChanged = false;
  possedeEnergieVHasChanged = false;

  onPolicyAddressChanged() {
    this.policyAddressHasChanged = this.policyDetails.address !== this.policyDetailsCopy.address;
  }

  onPolicyAddressComplementChanged() {
    this.policyAddressComplementHasChanged = this.policyDetails.addressCompl !== this.policyDetailsCopy.addressCompl;
  }

  onPolicyZipCodeHasChanged() {
    this.policyZipCodeHasChanged = this.policyDetails.postalCode !== this.policyDetailsCopy.postalCode;
  }

  onPolicyCityChanged() {
    this.policyCityHasChanged = this.policyDetails.city !== this.policyDetailsCopy.city;
  }

  onNatureLogementChange() {
    this.natureResidenceHasChanged =
      this.natureLogement !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.NATURE_LOG)?.value;
    this.emitNatureLogement.emit(this.natureLogement);
  }

  onQualiteOccupantChange() {
    this.qualiteOccupantHasChanged =
      this.qualiteOccupant !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.NATURE_OCCU)?.value;
    this.emitQualiteOccupant.emit(this.qualiteOccupant);
  }

  onTypeHabitationChange() {
    this.typeHabitationHasChanged =
      this.typeHabitation !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.HAB_TYPE)?.value;
  }

  onNombrePiecesChange() {
    this.nombrePiecesHasChanged =
      this.nombrePieces !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.NBR_PIECES)?.value;
    this.emitNombrePieces.emit(this.nombrePieces);
  }

  onEtageChange() {
    const riskItemInfo = this.policyDetailsCopy.riskItemInformations.values.find(
      x => x.code === this.riskItemInfo.ADR_ETAGE
    );

    if (riskItemInfo) {
      riskItemInfo.value = this.etage;
    }
    this.etageHasChanged =
      this.policyDetails.riskItemInformations.values.find(x => x.code === this.riskItemInfo.ADR_ETAGE)?.value !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.ADR_ETAGE)?.value;
  }

  onNombrePiecesQuaranteChange() {
    this.nombrePiecesQuaranteHasChanged =
      this.nombrePiecesQuarante !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.NBR_PIECES_40M2)?.value;
    this.emitNombrePiecesQuarante.emit(this.nombrePiecesQuarante);
  }

  onSurfaceDependanceHasChange() {
    this.surfaceDependanceHasChanged =
      this.surfaceDependance !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.SURFACE_DEP)?.value;
    this.emitSurfaceDependance.emit(this.surfaceDependance);
  }

  onPossedeVerandaChange() {
    this.possedeVerandaHasChanged =
      this.possedeVeranda !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.POSSEDE_VERANDA)?.value;
    this.emitPossedeVeranda.emit(this.possedeVeranda);
  }

  onPossedeEnergieVChange() {
    this.possedeEnergieVHasChanged =
      this.possedeEnergieV !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.POSSEDE_ENERGIEV)
        ?.value;
    this.emitPossedeEnergieV.emit(this.possedeEnergieV);
  }

  onPossedePiscineChange() {
    this.possedePiscineHasChanged =
      this.possedePiscine !==
      this.policyDetailsCopy.riskItemInformations.values.find(x => x.code === this.riskItemInfo.POSSEDE_PISCINE)?.value;
    this.emitPossedePiscine.emit(this.possedePiscine);
  }
}
